import { CertificateProgramEngagementActionBlock } from '@mc/api'
import { LogoMonogram } from '@mc/design-system'
import Experiment from '@mc/experiments-react'
import {
  ExperimentVariable,
  StyledVariable,
  TEXT_KEY,
  URGENCY_TEXT_VARIABLE_NAME,
  UrgencyEntries,
  useUrgency,
} from '@mc/feature-variables'
import { useTranslation } from '@mc/i18n'
import { paths } from '@mc/paths'
import { MCCERTS_1928_SECONDARY_CTA_REMOVAL_FLAG } from 'pages/certificates/experiments/mccerts-1928-secondary-cta-removal'
import { PromoVariables } from 'pages/certificates/promo'
import { DownloadSyllabusButton } from '../DownloadSyllabus'
import { EngagementCTA } from '../EngagementActionBlock/EngagementCTA'
import { SignUpButton } from '../SignUpButton'

type Props = {
  promo: PromoVariables['promo']
  cta: NonNullable<PromoVariables['cta']>
  releaseLabel?: string
  setShowSyllabusModal: (show: boolean) => void
  showSyllabusButton?: boolean
  certUuid: string
  certSlug: string
  available: boolean
  defaultEngagement?: CertificateProgramEngagementActionBlock
  engagement?: CertificateProgramEngagementActionBlock
}

const URGENCY_CONFIG = {
  urgencyText: {
    variableName: URGENCY_TEXT_VARIABLE_NAME,
    variableKey: TEXT_KEY,
  },
} as UrgencyEntries

const DetailsSection = ({
  certUuid,
  defaultEngagement,
  setShowSyllabusModal,
  engagement,
}: {
  certUuid: string
  certSlug: string
  setShowSyllabusModal: (show: boolean) => void
  defaultEngagement?: CertificateProgramEngagementActionBlock
  engagement?: CertificateProgramEngagementActionBlock
}) => {
  const finalEngagement = engagement || defaultEngagement

  if (!finalEngagement || !finalEngagement.details) return null

  return (
    <div className='d-flex align-items-center c-mt-5'>
      {finalEngagement.details && (
        <div className='d-flex align-items-center'>
          <div
            className='mc-corners--3 mc-p-2'
            style={{ border: '2px solid var(--c-beige-700)' }}
          >
            <LogoMonogram width='1.5em' height='1.5em' />
          </div>
          <p className='mc-px-4 mc-text-small c-text-color--dark'>
            {finalEngagement?.details?.text}
            {finalEngagement.details?.link && (
              <EngagementCTA
                action={finalEngagement.details.link}
                setShowSyllabusModal={setShowSyllabusModal}
                certUuid={certUuid}
              />
            )}
          </p>
        </div>
      )}
    </div>
  )
}

export const EngagementPromo = ({
  promo,
  cta,
  releaseLabel,
  setShowSyllabusModal,
  showSyllabusButton = false,
  certUuid,
  certSlug,
  available,
  defaultEngagement,
  engagement,
}: Props) => {
  const { t } = useTranslation('@mc/certificates')

  const urgencies = useUrgency({
    entries: URGENCY_CONFIG,
    variables: cta as unknown as ExperimentVariable,
  })

  const showTrial = available && engagement?.details

  return (
    <div className='c-bg-base-300 mc-corners--lg c-p-5'>
      <div className='d-flex flex-column flex-md-row align-items-stretch align-items-md-center c-gap-3'>
        <div>
          <h2 className='c-body-bold c-mb-1'>
            <StyledVariable text={cta.text} />
            {releaseLabel &&
              t('marketingPages.promo.releaseLabel', {
                releaseLabel,
              })}
          </h2>
          <div className='d-flex justify-content-start align-items-start c-gap-2'>
            {urgencies.urgencyText && (
              <p className='mc-text-small c-text-color--tint-3'>
                <StyledVariable text={urgencies.urgencyText} />
              </p>
            )}
            <a
              className='mc-text-small mc-text--link c-text-color--tint-3'
              href={promo.terms || paths.terms}
              target='_blank'
              rel='noopener noreferrer'
            >
              {t('marketingPages.promo.termsLink')}
            </a>
          </div>
        </div>
        <div className='d-flex c-gap-3 flex-column'>
          <SignUpButton arrow />
          {showSyllabusButton && (
            <Experiment
              name={MCCERTS_1928_SECONDARY_CTA_REMOVAL_FLAG}
              track={false}
              control={() => (
                <DownloadSyllabusButton
                  kind='tertiary'
                  onClick={() => {
                    setShowSyllabusModal(true)
                  }}
                />
              )}
              variant_1={() => null}
              variant_2={() => null}
            />
          )}
        </div>
      </div>

      {showTrial && (
        <DetailsSection
          setShowSyllabusModal={setShowSyllabusModal}
          certUuid={certUuid}
          certSlug={certSlug}
          defaultEngagement={defaultEngagement}
          engagement={engagement}
        />
      )}
    </div>
  )
}
